import { useCallback, useEffect, useRef } from 'react';

const Cruz = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1">
      <path d="M0.5 0.5 12.5 12.5" />
      <path d="M12.5 0.5 0.5 12.5" />
    </g>
  </svg>
);

const Flecha = ({ hacia }) => (
  <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden="true">
    <g
      stroke="currentColor"
      strokeWidth="1"
      transform={hacia === 'izq' ? 'translate(17 0) scale(-1 1)' : undefined}
    >
      <path d="M0 5.5h15.5" />
      <path d="M11.4 1.2 15.7 5.5l-4.3 4.3" />
    </g>
  </svg>
);

const dosDigitos = (n) => String(n).padStart(2, '0');

/**
 * El visor: una foto de la galeria, sola y en grande, sobre el resto del sitio.
 *
 * Se abre tocando cualquier foto del carrusel y se mueve de cuatro maneras, que
 * son las cuatro que la gente prueba: las flechas de la pantalla, las del
 * teclado, el dedo de lado, y tocando la propia foto para seguir. Se cierra con
 * Esc, con la cruz, o tocando el fondo.
 *
 * No da la vuelta al llegar al final, igual que el carrusel de abajo: las
 * flechas se apagan en los extremos y el contador dice donde esta uno. Dos
 * maneras distintas de contar la misma galeria confunden mas de lo que ayudan.
 *
 * Mientras esta abierto se le traba el scroll al body. Sin eso, deslizar sobre
 * la foto termina moviendo la pagina de atras, y al cerrar uno aparece en otra
 * parte del sitio sin saber por que.
 */
export default function Visor({ fotos, indice, onCerrar, onIr }) {
  const caja = useRef(null);
  const devolverFoco = useRef(null);

  const abierto = indice !== null && indice >= 0 && indice < fotos.length;
  const hayAnterior = abierto && indice > 0;
  const haySiguiente = abierto && indice < fotos.length - 1;

  const ir = useCallback(
    (paso) => {
      if (paso < 0 && !hayAnterior) return;
      if (paso > 0 && !haySiguiente) return;
      onIr(paso);
    },
    [hayAnterior, haySiguiente, onIr]
  );

  /* Teclado: Esc cierra, las flechas pasan de foto. */
  useEffect(() => {
    if (!abierto) return undefined;

    const tecla = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCerrar();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        ir(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        ir(1);
      }
    };

    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [abierto, ir, onCerrar]);

  /*
   * Se le traba el scroll al fondo. Se compensa el ancho de la barra para que
   * la pagina de atras no pegue un salto de lado al abrir el visor.
   */
  useEffect(() => {
    if (!abierto) return undefined;

    const { body } = document;
    const barra = window.innerWidth - document.documentElement.clientWidth;
    const overflow = body.style.overflow;
    const relleno = body.style.paddingRight;

    body.style.overflow = 'hidden';
    if (barra > 0) body.style.paddingRight = `${barra}px`;

    return () => {
      body.style.overflow = overflow;
      body.style.paddingRight = relleno;
    };
  }, [abierto]);

  /* El foco entra al visor y vuelve, al cerrar, a la foto desde la que se abrio. */
  useEffect(() => {
    if (!abierto) return undefined;

    devolverFoco.current = document.activeElement;
    caja.current?.focus();

    return () => {
      const volver = devolverFoco.current;
      if (volver && typeof volver.focus === 'function') volver.focus();
    };
  }, [abierto]);

  /* El dedo de lado, que en una galeria de fotos es lo primero que se intenta. */
  const tocado = useRef(null);

  const tocaEmpieza = (e) => {
    const t = e.changedTouches[0];
    tocado.current = { x: t.clientX, y: t.clientY };
  };

  const tocaTermina = (e) => {
    if (!tocado.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - tocado.current.x;
    const dy = t.clientY - tocado.current.y;
    tocado.current = null;
    /* Solo si fue claramente horizontal: si no, se come el scroll vertical. */
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) ir(dx < 0 ? 1 : -1);
  };

  if (!abierto) return null;

  const foto = fotos[indice];

  return (
    <div
      className="visor"
      ref={caja}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={`Foto ${indice + 1} de ${fotos.length}: ${foto.alt}`}
      onClick={(e) => {
        /* Tocar el fondo cierra; tocar la foto la pasa. */
        if (e.target === e.currentTarget) onCerrar();
      }}
      onTouchStart={tocaEmpieza}
      onTouchEnd={tocaTermina}
    >
      <div className="visor__barra">
        <p className="visor__cuenta">
          {dosDigitos(indice + 1)} <span aria-hidden="true">/</span> {dosDigitos(fotos.length)}
        </p>
        <button type="button" className="mando visor__cerrar" onClick={onCerrar} aria-label="Cerrar">
          <Cruz />
        </button>
      </div>

      {/*
        * Tocar la foto pasa a la siguiente, que es lo que la mano hace sola.
        * Para quien navega con lector de pantalla es un atajo repetido —las
        * flechas de abajo hacen lo mismo y se llaman igual—, asi que se saca
        * del arbol: la foto ya se anuncia en el `aria-label` del dialogo y se
        * lee escrita en el pie. Un mismo control nombrado dos veces estorba
        * mas que ayuda.
        */}
      <button
        type="button"
        className="visor__foto"
        onClick={() => ir(1)}
        disabled={!haySiguiente}
        tabIndex={-1}
        aria-hidden="true"
      >
        <img src={foto.src} alt="" decoding="async" />
      </button>

      <div className="visor__pie">
        <p className="visor__alt">{foto.alt}</p>
        <div className="mandos visor__mandos">
          <button
            type="button"
            className="mando"
            onClick={() => ir(-1)}
            disabled={!hayAnterior}
            aria-label="Ver la foto anterior"
          >
            <Flecha hacia="izq" />
          </button>
          <button
            type="button"
            className="mando"
            onClick={() => ir(1)}
            disabled={!haySiguiente}
            aria-label="Ver la foto siguiente"
          >
            <Flecha hacia="der" />
          </button>
        </div>
      </div>
    </div>
  );
}
